import { Menu, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { DisputeReasonForm } from 'app/Private/Reasons/DisputeReasonForm/DisputeReasonForm';
import { PauseReasonForm } from 'app/Private/Reasons/PauseReasonForm/PauseReasonForm';
import { UnrecoverableReasonForm } from 'app/Private/Reasons/UnrecoverableReasonForm/UnrecoverableReasonForm';
import classNames from 'classnames/bind';
import { i18nKeys, useTranslation } from 'locales';
import AYSModal from 'shared/components/AYSModal';
import { useCanUseManualActions, useIsConnectorActive } from 'shared/utils/connector';
import { invoiceActions } from 'store/invoice/invoice.actions';
import { DialogShowId, DialogShowSize, dialogHide, showDialog } from 'store/view/view.actions';
import type { Invoice, InvoiceActions } from 'types/store/invoice-state';

import styleIdentifiers from './markedInvoiceActions.module.scss';

const styles = classNames.bind(styleIdentifiers);

type Props = {
  element: React.ReactElement;
  invoice: Invoice;
};

enum Flags {
  DISPUTE = 'dispute',
  UNRECOVERABLE = 'unrecoverable',
  PAUSE = 'pause',
}

export default function MarkedInvoiceActions({ invoice, element }: Props) {
  const { t } = useTranslation();

  const checkIfInactive = (type: InvoiceActions) => !invoice?.actions.includes(type);
  const hasManualActionsActive = useCanUseManualActions();
  const hasActiveConnector = useIsConnectorActive();

  const reloadInvoice = () => {
    invoiceActions.info({
      id: invoice.id,
      noReset: true,
      noLoading: true,
    });
  };

  const handlePause = (values: Object) =>
    new Promise<void>(resolve => {
      invoiceActions.actionsPause({
        id: invoice.id,
        data: values,
        callback: () => {
          reloadInvoice();
          resolve();
        },
        actionFailure: () => resolve(),
      });
    });

  const handleUnrecoverable = (values: Object) =>
    new Promise<void>(resolve => {
      invoiceActions.actionsMarkAsUnrecoverable({
        id: invoice.id,
        data: values,
        callback: () => {
          reloadInvoice();
          resolve();
        },
        actionFailure: () => resolve(),
      });
    });

  const handleDispute = (values: any) =>
    new Promise<void>(resolve => {
      invoiceActions.actionsDispute({
        id: invoice.id,
        data: { ...values, dispute: true },
        callback: () => {
          reloadInvoice();
          resolve();
        },
        actionFailure: () => resolve(),
      });
    });

  const toggleClose = () => {
    invoiceActions.actionsPause({
      id: invoice.id,
      callback: reloadInvoice,
    });
  };

  const markAsPaid = () => {
    if (checkIfInactive('mark_as_paid')) return;

    modals.openConfirmModal({
      title: t(i18nKeys.INVOICE.PAID),
      centered: true,
      children: <Text size="sm">{t(i18nKeys.AYS.INVOICE_ACTION_MARK_AS_PAID)}</Text>,
      labels: { confirm: t(i18nKeys.CONFIRM), cancel: t(i18nKeys.CANCEL) },
      onConfirm: () => {
        invoiceActions.actionsMarkAsPaid({
          id: invoice.id,
          callback: reloadInvoice,
        });
      },
    });
  };

  const dispute = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.SMALL,
      title: t(i18nKeys.INVOICE.DISPUTED),
      children: (
        <AYSModal
          text={t(i18nKeys.AYS.INVOICE_ACTION_UNDISPUTE)}
          onConfirm={() => {
            invoiceActions.actionsDispute({
              id: invoice.id,
              data: {
                dispute: false,
              },
              actionFailure: () => {
                dialogHide(DialogShowId.CUSTOM);
              },
              callback: () => {
                reloadInvoice();
                dialogHide(DialogShowId.CUSTOM);
              },
            });
          }}
        />
      ),
    });
  };

  const handleFlag = (actionType: string) => {
    switch (actionType) {
      case Flags.PAUSE:
        return handlePause;
      case Flags.UNRECOVERABLE:
        return handleUnrecoverable;
      default:
        return handleDispute;
    }
  };

  const flag = (actionType: string, title: string) => {
    const onSubmit = handleFlag(actionType);

    modals.open({
      size: 'sm',
      title: t(title),
      children:
        actionType === Flags.PAUSE ? (
          <PauseReasonForm onSubmit={onSubmit} />
        ) : actionType === Flags.UNRECOVERABLE ? (
          <UnrecoverableReasonForm onSubmit={onSubmit} />
        ) : (
          <DisputeReasonForm onSubmit={onSubmit} />
        ),
    });
  };

  return (
    <Menu position="bottom-end" width={260}>
      <Menu.Target>
        <div className={styles('marked-invoice-actions')}>{element}</div>
      </Menu.Target>
      <Menu.Dropdown>
        {hasManualActionsActive && (
          <Menu.Item disabled={checkIfInactive('mark_as_paid')} onClick={markAsPaid}>
            {t(i18nKeys.INVOICE.MARK_AS_PAID)}
          </Menu.Item>
        )}
        <Menu.Item
          disabled={checkIfInactive('mark_as_unrecoverable') || hasActiveConnector}
          onClick={() => flag(Flags.UNRECOVERABLE, i18nKeys.INVOICE.UNRECOVERABLE)}
        >
          {t(i18nKeys.INVOICE.MARK_AS_UNRECOVERABLE)}
        </Menu.Item>
        {checkIfInactive('undispute') ? (
          <Menu.Item
            disabled={checkIfInactive(Flags.DISPUTE)}
            onClick={() => flag(Flags.DISPUTE, i18nKeys.INVOICE.DISPUTED)}
          >
            {t(i18nKeys.INVOICE.MARK_AS_DISPUTED)}
          </Menu.Item>
        ) : (
          <Menu.Item onClick={dispute}>{t(i18nKeys.INVOICE.UNDO_DISPUTED)}</Menu.Item>
        )}
        {!checkIfInactive(Flags.PAUSE) && (
          <Menu.Item onClick={() => flag(Flags.PAUSE, i18nKeys.CLIENT.PAUSE)}>
            {t(i18nKeys.INVOICE.PAUSE_THE_FOLLOW_UP)}
          </Menu.Item>
        )}
        {!checkIfInactive('unpause') && (
          <Menu.Item onClick={toggleClose}>{t(i18nKeys.INVOICE.RESUME_FOLLUW_UP)}</Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
